import React, { useState } from 'react';
import { theme } from '../../../theme';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  Platform,
  StyleSheet,
  Animated,
  Linking,
} from 'react-native';
import { isTablet } from 'react-native-device-info';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import { fs } from '../../../utils/libs';

interface FAQProps {
  children: React.ReactNode;
  defaultOpen?: number[];
}

interface QAItemProps {
  children: React.ReactNode;
  isOpen?: boolean;
  onToggle?: () => void;
  id?: number;
}

interface QuestionProps {
  title: string;
  isOpen?: boolean;
  onToggle?: () => void;
  answerId?: number;
}

interface AnswerProps {
  children: React.ReactNode;
  isOpen?: boolean;
  id?: number;
}

function FAQ({ children, defaultOpen = [0] }: FAQProps) {
  const [open, setIsOpen] = useState(defaultOpen);

  const isOpen = (index: number) => open.includes(index);

  const onToggle = (index: number) => {
    if (open.includes(index)) {
      setIsOpen(open.filter(item => item !== index));
    } else {
      setIsOpen([...open, index]);
    }
  };

  return (
    <View style={styles.faqContainer}>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<QAItemProps>, {
            isOpen: isOpen(index),
            onToggle: () => onToggle(index),
          });
        }
        return child;
      })}
    </View>
  );
}

function Question({ title, isOpen, onToggle }: QuestionProps) {
  return (
    <Pressable
      style={({ pressed, hovered }: any) => [
        styles.questionContainer,
        Platform.OS === 'web' && styles.questionWeb,
        Platform.OS === 'web' && hovered && styles.questionHovered,
        pressed && { opacity: 0.85 },
      ]}
      onPress={onToggle}>
      <Text style={styles.questionText}>{title}</Text>
      <Ionicons
        name={isOpen ? 'chevron-up' : 'chevron-down'}
        size={24}
        color={theme.colors.textGray600}
      />
    </Pressable>
  );
}

function Answer({ children, isOpen }: AnswerProps) {
  const animatedHeight = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    Animated.timing(animatedHeight, {
      toValue: isOpen ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [isOpen]);

  const renderTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <TouchableOpacity key={index} onPress={() => Linking.openURL(part)}>
            <Text style={styles.linkText}>{part}</Text>
          </TouchableOpacity>
        );
      }
      return <Text key={index}>{part}</Text>;
    });
  };

  return (
    <Animated.View
      style={[
        styles.answerContainer,
        {
          maxHeight: animatedHeight.interpolate({
            inputRange: [0, 1],
            outputRange: [0, 1000],
          }),
          opacity: animatedHeight,
        },
      ]}>
      <Text style={styles.answerText}>
        {renderTextWithLinks(children as string)}
      </Text>
    </Animated.View>
  );
}

function QAItem({ children, isOpen, onToggle }: QAItemProps) {
  return (
    <View style={styles.qaItemContainer}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(
            child as React.ReactElement<QuestionProps | AnswerProps>,
            {
              isOpen: isOpen,
              onToggle: onToggle,
            },
          );
        }
        return child;
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  faqContainer: {
    gap: 16,
  },
  qaItemContainer: {
    marginBottom: 16,
    // backgroundColor: "white",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(156, 163, 175, 0.25)',
    shadowColor: theme.colors.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    backgroundColor: theme.colors.secondaryLight,
  },
  questionContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  questionText: {
    fontSize: 17,
    fontWeight: '600',
    color: theme.colors.shadow,
    flex: 1,
    paddingRight: 16,
  },
  answerContainer: {
    overflow: 'hidden',
  },
  answerText: {
    fontSize: fs(isTablet() ? 18 : 16),
    color: theme.colors.textGray700,
    padding: 16,
    paddingTop: 0,
  },
  linkText: {
    color: theme.colors.link,
    fontSize: fs(isTablet() ? 18 : 16),
    lineHeight: fs(isTablet() ? 22 : 20),
    textDecorationLine: 'underline',
  },
  questionWeb: {
    ...(Platform.OS === 'web'
      ? ({
          cursor: 'pointer',
          transition: 'background-color 0.15s ease-out',
        } as any)
      : {}),
  },
  questionHovered: {
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
});

FAQ.QAItem = QAItem;
FAQ.Question = Question;
FAQ.Answer = Answer;

export default FAQ;
